import React, { useState, useEffect, useCallback } from 'react';
import {
    Key,
    Plus,
    Copy,
    Check,
    Edit2,
    Trash2,
    RefreshCw,
    BarChart3,
    BookOpen,
    AlertCircle,
    Loader2,
    X,
    Crown,
    ChevronRight,
    Calendar,
    TrendingUp,
    Zap,
    CreditCard,
    Clock,
    Activity,
    CheckCircle2,
    XCircle,
    Download,
    ChevronLeft,
    ArrowUpRight,
    Search,
    Eye,
    Server,
    Globe
} from 'lucide-react';
import {
    createApiKey,
    fetchApiKeys,
    updateApiKey,
    deleteApiKey,
    rotateApiKey,
    fetchUsageSummary,
    fetchDailyUsage,
    fetchPermissionScopes,
    fetchRequestLogs,
    fetchUsageAnalytics,
    exportUsageData
} from '../api/developer';

// Documentation Content Component
const DocContent = ({ copyToClipboard, copiedKey, userMeta }) => {
    const [activeSection, setActiveSection] = useState('introduction');

    const sections = [
        { id: 'introduction', label: 'Overview' },
        { id: 'authentication', label: 'Authentication' },
        { id: 'cases', label: 'Cases API' },
        { id: 'documents', label: 'Documents API' },
        { id: 'reports', label: 'Reports API' },
        { id: 'usage', label: 'Usage & Account' },
        { id: 'rate-limits', label: 'Rate Limiting' },
        { id: 'errors', label: 'Error Reference' },
        { id: 'typescript', label: 'TypeScript Client' }
    ];

    const CodeBlock = ({ code, language = 'bash', id }) => (
        <div className="relative group">
            <pre className="dark:bg-slate-950 bg-slate-100 border dark:border-slate-800 border-slate-200 rounded-lg p-4 overflow-x-auto text-sm">
                <code className="font-mono dark:text-slate-300 text-slate-800">{code}</code>
            </pre>
            <button
                onClick={() => copyToClipboard(code, id)}
                className="absolute top-3 right-3 p-2 dark:bg-slate-800 bg-slate-200 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-sky-600 hover:text-white"
                title="Copy code"
            >
                {copiedKey === id ? (
                    <Check className="w-4 h-4" />
                ) : (
                    <Copy className="w-4 h-4" />
                )}
            </button>
        </div>
    );

    return (
        <div className="flex gap-6 relative">
            {/* Table of Contents Sidebar */}
            <div className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-6 dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                    <h3 className="text-sm font-bold dark:text-white text-slate-900 mb-4 uppercase tracking-wider">
                        Table of Contents
                    </h3>
                    <nav className="space-y-1">
                        {sections.map((section) => (
                            <a
                                key={section.id}
                                href={`#${section.id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveSection(section.id);
                                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                    activeSection === section.id
                                        ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400 font-medium'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                            >
                                {section.label}
                            </a>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Documentation Content */}
            <div className="flex-1 dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-8 overflow-y-auto max-h-[calc(100vh-16rem)]">
                {/* Introduction / Overview */}
                <section id="introduction" className="mb-12 scroll-mt-6">
                    <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-4">
                        CloudMortgage Developer API
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        The Developer Portal provides external API access for Ultra and Enterprise subscription tiers. This documentation covers the v1 Public API endpoints for managing cases, documents, and AI-powered underwriting reports.
                    </p>
                    <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-4 mb-6">
                        <p className="text-sm text-sky-600 dark:text-sky-400 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                                <strong>Base URL:</strong> <code className="dark:bg-slate-950 bg-slate-200 px-2 py-1 rounded">https://api.cloudmortgage.io</code>
                            </span>
                        </p>
                    </div>

                    {/* Verified Endpoints Table */}
                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-3">Available Endpoints</h3>
                    <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm">
                            <thead className="dark:bg-slate-800 bg-slate-100">
                                <tr>
                                    <th className="text-left px-4 py-2 dark:text-slate-300 text-slate-700">Endpoint</th>
                                    <th className="text-left px-4 py-2 dark:text-slate-300 text-slate-700">Method</th>
                                    <th className="text-left px-4 py-2 dark:text-slate-300 text-slate-700">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-slate-700 divide-slate-200">
                                {[
                                    { endpoint: '/v1/cases', method: 'GET', status: 'Working' },
                                    { endpoint: '/v1/cases', method: 'POST', status: 'Working' },
                                    { endpoint: '/v1/cases/{case_id}', method: 'GET', status: 'Working' },
                                    { endpoint: '/v1/cases/{case_id}', method: 'PATCH', status: 'Working' },
                                    { endpoint: '/v1/cases/{case_id}/documents', method: 'GET', status: 'Working' },
                                    { endpoint: '/v1/cases/{case_id}/documents', method: 'POST', status: 'Working' },
                                    { endpoint: '/v1/cases/{case_id}/reports', method: 'POST', status: 'Working' },
                                    { endpoint: '/v1/reports/{run_id}', method: 'GET', status: 'Working' },
                                    { endpoint: '/v1/reports/{run_id}/content', method: 'GET', status: 'Working' },
                                    { endpoint: '/v1/usage', method: 'GET', status: 'Working' },
                                    { endpoint: '/v1/account', method: 'GET', status: 'Working' },
                                ].map((row, idx) => (
                                    <tr key={idx} className="dark:hover:bg-slate-800/50 hover:bg-slate-50">
                                        <td className="px-4 py-2 font-mono text-xs dark:text-slate-300 text-slate-700">{row.endpoint}</td>
                                        <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${row.method === 'GET' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : row.method === 'POST' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'}`}>{row.method}</span></td>
                                        <td className="px-4 py-2 text-green-600 dark:text-green-400">✓ {row.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Authentication */}
                <section id="authentication" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Authentication</h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        All API requests require authentication using an API key. API keys follow this format:
                    </p>
                    <CodeBlock
                        id="api-key-format"
                        code={`cm_{environment}_sk_{random_40_chars}

Examples:
Live: cm_live_sk_a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8
Test: cm_test_sk_z9y8x7w6v5u4t3s2r1q0p9o8n7m6l5k4j3`}
                    />

                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mt-6 mb-3">Authentication Methods</h3>
                    <div className="space-y-4">
                        <div className="dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-lg p-4">
                            <p className="text-sm font-semibold dark:text-white text-slate-900 mb-2">Option 1: X-API-Key Header (Recommended)</p>
                            <code className="text-sm dark:text-slate-300 text-slate-700">X-API-Key: cm_live_sk_your_key_here</code>
                        </div>
                        <div className="dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-lg p-4">
                            <p className="text-sm font-semibold dark:text-white text-slate-900 mb-2">Option 2: Bearer Token</p>
                            <code className="text-sm dark:text-slate-300 text-slate-700">Authorization: Bearer cm_live_sk_your_key_here</code>
                        </div>
                    </div>

                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mt-6 mb-3">Example Request</h3>
                    <CodeBlock
                        id="auth-example"
                        code={`curl -X GET "https://api.cloudmortgage.io/v1/cases" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                    />
                    <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                        <p className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            Keep your API keys secure! Never share them publicly or commit them to version control.
                        </p>
                    </div>
                </section>

                {/* Cases API */}
                <section id="cases" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Cases API</h2>

                    {/* POST /v1/cases */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 rounded text-xs font-bold">POST</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases</code>
                            <span className="text-xs text-slate-500">Create Case • 10 credits</span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 mb-4">Create a new mortgage case.</p>
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-2">Request</h4>
                        <CodeBlock
                            id="create-case-req"
                            code={`curl -X POST "https://api.cloudmortgage.io/v1/cases" \\
  -H "X-API-Key: cm_live_sk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Doe",
    "property_address": "123 Main Street, Austin, TX 78701",
    "loan_type": "Conventional",
    "loan_amount": 350000,
    "borrower_email": "john.doe@example.com"
  }'`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (201 Created)</h4>
                        <CodeBlock
                            id="create-case-res"
                            language="json"
                            code={`{
  "case_id": "case-abc123def456",
  "case_number": "CN-2026-001",
  "name": "John Doe",
  "property_address": "123 Main Street, Austin, TX 78701",
  "loan_type": "Conventional",
  "loan_amount": 350000,
  "status": "New",
  "created_at": "2026-01-17T10:30:00Z",
  "updated_at": null
}`}
                        />
                    </div>

                    {/* GET /v1/cases */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases</code>
                            <span className="text-xs text-slate-500">List Cases • Free</span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 mb-4">List all cases with optional filtering.</p>
                        <div className="overflow-x-auto mb-4">
                            <table className="w-full text-sm">
                                <thead className="dark:bg-slate-800 bg-slate-100">
                                    <tr>
                                        <th className="text-left px-3 py-2">Parameter</th>
                                        <th className="text-left px-3 py-2">Type</th>
                                        <th className="text-left px-3 py-2">Default</th>
                                        <th className="text-left px-3 py-2">Description</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y dark:divide-slate-700 divide-slate-200">
                                    <tr><td className="px-3 py-2 font-mono text-xs">limit</td><td className="px-3 py-2">integer</td><td className="px-3 py-2">20</td><td className="px-3 py-2">Max results (1-100)</td></tr>
                                    <tr><td className="px-3 py-2 font-mono text-xs">cursor</td><td className="px-3 py-2">string</td><td className="px-3 py-2">null</td><td className="px-3 py-2">Pagination cursor</td></tr>
                                    <tr><td className="px-3 py-2 font-mono text-xs">status</td><td className="px-3 py-2">string</td><td className="px-3 py-2">null</td><td className="px-3 py-2">Filter by status</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-2">Request</h4>
                        <CodeBlock
                            id="list-cases-req"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/cases?status=New&limit=10" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (200 OK)</h4>
                        <CodeBlock
                            id="list-cases-res"
                            language="json"
                            code={`{
  "cases": [
    {
      "case_id": "case-abc123def456",
      "case_number": "CN-2026-001",
      "name": "John Doe",
      "property_address": "123 Main Street, Austin, TX 78701",
      "loan_type": "Conventional",
      "loan_amount": 350000,
      "status": "New",
      "created_at": "2026-01-17T10:30:00Z",
      "updated_at": null
    }
  ],
  "total": 45,
  "has_more": true,
  "next_cursor": null
}`}
                        />
                    </div>

                    {/* GET /v1/cases/{case_id} */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases/{'{case_id}'}</code>
                            <span className="text-xs text-slate-500">Get Case Details • Free</span>
                        </div>
                        <CodeBlock
                            id="get-case-req"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/cases/case-abc123def456" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                    </div>

                    {/* PATCH /v1/cases/{case_id} */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 rounded text-xs font-bold">PATCH</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases/{'{case_id}'}</code>
                            <span className="text-xs text-slate-500">Update Case • 5 credits</span>
                        </div>
                        <CodeBlock
                            id="update-case-req"
                            code={`curl -X PATCH "https://api.cloudmortgage.io/v1/cases/case-abc123def456" \\
  -H "X-API-Key: cm_live_sk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John M. Doe",
    "status": "Under Review"
  }'`}
                        />
                    </div>
                </section>

                {/* Documents API */}
                <section id="documents" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Documents API</h2>

                    {/* GET /v1/cases/{case_id}/documents */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases/{'{case_id}'}/documents</code>
                            <span className="text-xs text-slate-500">List Documents • Free</span>
                        </div>
                        <CodeBlock
                            id="list-docs-req"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/cases/case-abc123def456/documents" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (200 OK)</h4>
                        <CodeBlock
                            id="list-docs-res"
                            language="json"
                            code={`{
  "documents": [
    {
      "document_id": "doc-pay123",
      "case_id": "case-abc123def456",
      "file_name": "paystub_january_2026.pdf",
      "document_type": "pay_stub",
      "file_size": 245678,
      "content_type": "application/pdf",
      "status": "uploaded",
      "created_at": "2026-01-17T11:00:00Z",
      "download_url": "https://s3.amazonaws.com/cloudmortgage-docs/..."
    }
  ],
  "total": 2
}`}
                        />
                    </div>

                    {/* POST /v1/cases/{case_id}/documents */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 rounded text-xs font-bold">POST</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases/{'{case_id}'}/documents</code>
                            <span className="text-xs text-slate-500">Get Upload URL • 5 credits</span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 mb-4">Request a presigned URL for document upload.</p>
                        <CodeBlock
                            id="upload-doc-req"
                            code={`curl -X POST "https://api.cloudmortgage.io/v1/cases/case-abc123def456/documents" \\
  -H "X-API-Key: cm_live_sk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file_name": "bank_statement_dec_2025.pdf",
    "document_type": "bank_statement"
  }'`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (201 Created)</h4>
                        <CodeBlock
                            id="upload-doc-res"
                            language="json"
                            code={`{
  "document_id": "doc-bank789",
  "upload_url": "https://s3.amazonaws.com/cloudmortgage-docs/...",
  "expires_in": 3600
}`}
                        />
                    </div>
                </section>

                {/* Reports API */}
                <section id="reports" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Reports API</h2>

                    {/* POST /v1/cases/{case_id}/reports */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 rounded text-xs font-bold">POST</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/cases/{'{case_id}'}/reports</code>
                            <span className="text-xs text-slate-500">Generate Report • 1000-3000 credits</span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 mb-4">Generate an AI-powered underwriting report. Basic: 1000 credits, Advanced: 3000 credits.</p>
                        <CodeBlock
                            id="gen-report-req"
                            code={`curl -X POST "https://api.cloudmortgage.io/v1/cases/case-abc123def456/reports" \\
  -H "X-API-Key: cm_live_sk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "report_type": "advanced",
    "include_recommendations": true
  }'`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (202 Accepted)</h4>
                        <CodeBlock
                            id="gen-report-res"
                            language="json"
                            code={`{
  "run_id": "run-2026-01-17-16-30-00",
  "case_id": "case-abc123def456",
  "status": "PENDING",
  "progress": 0,
  "report_type": "advanced",
  "started_at": "2026-01-17T16:30:00Z",
  "completed_at": null,
  "error": null,
  "credits_used": 3000
}`}
                        />
                    </div>

                    {/* GET /v1/reports/{run_id} */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/reports/{'{run_id}'}</code>
                            <span className="text-xs text-slate-500">Get Report Status • Free</span>
                        </div>
                        <CodeBlock
                            id="get-report-status"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/reports/run-2026-01-17-16-30-00" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (200 OK - Completed)</h4>
                        <CodeBlock
                            id="get-report-status-res"
                            language="json"
                            code={`{
  "run_id": "run-2026-01-17-16-30-00",
  "case_id": "case-abc123def456",
  "status": "COMPLETED",
  "progress": 100,
  "report_type": "advanced",
  "started_at": "2026-01-17T16:30:00Z",
  "completed_at": "2026-01-17T16:45:00Z",
  "error": null,
  "credits_used": 3000
}`}
                        />
                    </div>

                    {/* GET /v1/reports/{run_id}/content */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/reports/{'{run_id}'}/content</code>
                            <span className="text-xs text-slate-500">Get Report Content • Free</span>
                        </div>
                        <CodeBlock
                            id="get-report-content"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/reports/run-2026-01-17-16-30-00/content" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (200 OK)</h4>
                        <CodeBlock
                            id="get-report-content-res"
                            language="json"
                            code={`{
  "run_id": "run-2026-01-17-16-30-00",
  "case_id": "case-abc123def456",
  "status": "COMPLETED",
  "content": {
    "borrower_profile": {
      "name": "John Doe",
      "credit_score": 745,
      "dti_ratio": 32.5,
      "employment_status": "Verified"
    },
    "property_analysis": {
      "address": "123 Main Street, Austin, TX 78701",
      "appraised_value": 425000,
      "ltv_ratio": 82.4
    },
    "risk_assessment": {
      "overall_risk": "Low",
      "risk_score": 85,
      "flags": []
    }
  },
  "summary": "This loan application presents a low-risk profile...",
  "recommendations": [
    "Verify final employment status within 10 days of closing",
    "Confirm homeowner's insurance coverage"
  ],
  "generated_at": "2026-01-17T16:45:00Z"
}`}
                        />
                    </div>
                </section>

                {/* Usage & Account */}
                <section id="usage" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Usage & Account</h2>

                    {/* GET /v1/usage */}
                    <div className="mb-8 border-b dark:border-slate-700 border-slate-200 pb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/usage</code>
                            <span className="text-xs text-slate-500">Get Current Usage • Free</span>
                        </div>
                        <CodeBlock
                            id="get-usage"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/usage" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (200 OK)</h4>
                        <CodeBlock
                            id="get-usage-res"
                            language="json"
                            code={`{
  "api_key_id": "ak_prod_abc123",
  "period_start": "2026-01-01T00:00:00Z",
  "period_end": "2026-01-31T23:59:59Z",
  "requests_made": 1247,
  "requests_limit": 144000,
  "credits_used": 15500,
  "credits_remaining": 34500,
  "rate_limit_rpm": 100
}`}
                        />
                    </div>

                    {/* GET /v1/account */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 rounded text-xs font-bold">GET</span>
                            <code className="text-sm dark:text-slate-300 text-slate-700">/v1/account</code>
                            <span className="text-xs text-slate-500">Get Account Info • Free</span>
                        </div>
                        <CodeBlock
                            id="get-account"
                            code={`curl -X GET "https://api.cloudmortgage.io/v1/account" \\
  -H "X-API-Key: cm_live_sk_your_key_here"`}
                        />
                        <h4 className="text-sm font-semibold dark:text-white text-slate-900 mt-4 mb-2">Response (200 OK)</h4>
                        <CodeBlock
                            id="get-account-res"
                            language="json"
                            code={`{
  "user_id": "developer@company.com",
  "tier": "ultra",
  "tier_display_name": "Ultra",
  "api_access": true,
  "credits_remaining": 34500,
  "credits_monthly": 50000,
  "api_keys_count": 2,
  "api_keys_limit": 3
}`}
                        />
                    </div>
                </section>

                {/* Rate Limiting */}
                <section id="rate-limits" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Rate Limiting</h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        All responses include rate limit headers:
                    </p>
                    <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm">
                            <thead className="dark:bg-slate-800 bg-slate-100">
                                <tr>
                                    <th className="text-left px-4 py-2">Header</th>
                                    <th className="text-left px-4 py-2">Description</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-slate-700 divide-slate-200">
                                <tr><td className="px-4 py-2 font-mono text-xs">X-RateLimit-Limit</td><td className="px-4 py-2">Maximum requests per minute</td></tr>
                                <tr><td className="px-4 py-2 font-mono text-xs">X-RateLimit-Remaining</td><td className="px-4 py-2">Requests remaining in current window</td></tr>
                                <tr><td className="px-4 py-2 font-mono text-xs">X-RateLimit-Reset</td><td className="px-4 py-2">Unix timestamp when limit resets</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-lg p-4">
                            <h3 className="font-semibold dark:text-white text-slate-900 mb-2">Ultra Tier</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">100 requests per minute</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">Maximum 3 API keys</p>
                        </div>
                        <div className="dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-lg p-4">
                            <h3 className="font-semibold dark:text-white text-slate-900 mb-2">Enterprise Tier</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">1,000 requests per minute</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">Unlimited API keys</p>
                        </div>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        Rate limit information is included in response headers:
                    </p>
                    <CodeBlock
                        id="rate-limit-headers"
                        code={`X-RateLimit-Limit: 100
X-RateLimit-Remaining: 95
X-RateLimit-Reset: 1640000000`}
                    />
                </section>

                {/* Error Handling */}
                <section id="errors" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">Error Handling</h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        The API uses standard HTTP status codes. Errors return JSON with details:
                    </p>
                    <CodeBlock
                        id="error-response"
                        language="json"
                        code={`{
  "error": {
    "code": "invalid_request",
    "message": "The case_id parameter is required",
    "param": "case_id"
  }
}`}
                    />
                    <div className="mt-4 space-y-2">
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">200</code>
                            <span className="text-slate-600 dark:text-slate-400">Success</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">400</code>
                            <span className="text-slate-600 dark:text-slate-400">Bad Request - Invalid parameters</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">401</code>
                            <span className="text-slate-600 dark:text-slate-400">Unauthorized - Invalid API key</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">403</code>
                            <span className="text-slate-600 dark:text-slate-400">Forbidden - Insufficient permissions</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">404</code>
                            <span className="text-slate-600 dark:text-slate-400">Not Found - Resource doesn't exist</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">429</code>
                            <span className="text-slate-600 dark:text-slate-400">Too Many Requests - Rate limit exceeded</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <code className="dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded font-mono">500</code>
                            <span className="text-slate-600 dark:text-slate-400">Internal Server Error</span>
                        </div>
                    </div>
                </section>

                {/* TypeScript Client */}
                <section id="typescript" className="mb-12 scroll-mt-6">
                    <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-4">TypeScript Client</h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        Install our official TypeScript/JavaScript SDK for easier integration:
                    </p>
                    <CodeBlock
                        id="install-sdk"
                        code={`npm install @cloudmortgage/sdk
# or
yarn add @cloudmortgage/sdk`}
                    />

                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mt-8 mb-3">Usage Example</h3>
                    <CodeBlock
                        id="sdk-usage"
                        language="typescript"
                        code={`import { CloudMortgageClient } from '@cloudmortgage/sdk';

const client = new CloudMortgageClient({
  apiKey: process.env.CM_API_KEY  // cm_live_sk_...
});

// Create a case
const newCase = await client.cases.create({
  name: 'John Doe',
  property_address: '123 Main Street, Austin, TX 78701',
  loan_type: 'Conventional',
  loan_amount: 350000,
  borrower_email: 'john.doe@example.com'
});

// Upload a document
const doc = await client.documents.upload(newCase.case_id, {
  file: fs.createReadStream('./w2-2025.pdf'),
  document_type: 'w2',
  description: '2025 W-2 Form'
});

// Start AI analysis
const analysis = await client.reports.runAnalysis(newCase.case_id);

// Poll for completion
const result = await client.reports.waitForCompletion(analysis.run_id, {
  pollInterval: 2000,
  timeout: 300000
});`}
                    />

                    <h3 className="text-lg font-semibold dark:text-white text-slate-900 mt-8 mb-3">Error Handling</h3>
                    <CodeBlock
                        id="sdk-errors"
                        language="typescript"
                        code={`import { CloudMortgageClient, APIError, RateLimitError } from '@cloudmortgage/sdk';

try {
  const cases = await client.cases.list();
} catch (error) {
  if (error instanceof RateLimitError) {
  } else if (error instanceof APIError) {
  }
}`}
                    />
                </section>

                {/* Footer */}
                <div className="border-t dark:border-slate-800 border-slate-200 pt-6 mt-12">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Need help? Contact our support team at{' '}
                                <a href="mailto:api-support@cloudmortgage.io" className="text-sky-600 dark:text-sky-400 hover:underline">
                                    api-support@cloudmortgage.io
                                </a>
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <span>Current Balance:</span>
                            <span className="font-bold text-violet-600 dark:text-violet-400">
                                {(userMeta?.credit || 0).toLocaleString()} credits
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DeveloperPortal = ({ userMeta, onUpgradePlan, onTopUp, creditPacks = [], isPaymentProcessing = false }) => {
    const [selectedTab, setSelectedTab] = useState('keys'); // 'keys', 'usage', 'docs'
    const [keys, setKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);


    // Key creation
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [createdKey, setCreatedKey] = useState(null); // { key_id, key, key_prefix }

    // Key editing
    const [editingKey, setEditingKey] = useState(null);
    const [editKeyName, setEditKeyName] = useState('');
    const [editPermissions, setEditPermissions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Key deletion
    const [deletingKey, setDeletingKey] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Key rotation
    const [rotatingKey, setRotatingKey] = useState(null);
    const [isRotating, setIsRotating] = useState(false);
    const [rotatedKey, setRotatedKey] = useState(null);

    // Permissions
    const [availablePermissions, setAvailablePermissions] = useState([]);

    // Usage stats
    const [usageSummary, setUsageSummary] = useState(null);
    const [dailyUsage, setDailyUsage] = useState([]);
    const [isLoadingUsage, setIsLoadingUsage] = useState(false);

    // Enhanced usage stats
    const [requestLogs, setRequestLogs] = useState([]);
    const [usageAnalytics, setUsageAnalytics] = useState(null);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [logsPage, setLogsPage] = useState(1);
    const [logsTotalPages, setLogsTotalPages] = useState(1);
    const [usageSubTab, setUsageSubTab] = useState('overview'); // 'overview', 'logs', 'analytics'
    const [dateRange, setDateRange] = useState('7d'); // 'today', '7d', '30d', 'custom'
    const [customDateStart, setCustomDateStart] = useState('');
    const [customDateEnd, setCustomDateEnd] = useState('');
    const [logsFilter, setLogsFilter] = useState({
        status: 'all',
        endpoint: '',
        apiKeyId: '',
        search: ''
    });
    const [analyticsGroupBy, setAnalyticsGroupBy] = useState('endpoint');
    const [isExporting, setIsExporting] = useState(false);

    // Copy state
    const [copiedKey, setCopiedKey] = useState(null);

    // Top-up credits
    const [showTopUpModal, setShowTopUpModal] = useState(false);

    // Check if user has access (Ultra or Enterprise only)
    const currentTier = (userMeta?.membership || 'Free').toLowerCase();
    const hasAccess = currentTier === 'ultra' || currentTier === 'enterprise';

    // Tier limits
    const tierLimits = {
        ultra: { maxKeys: 3, rpm: 100 },
        enterprise: { maxKeys: Infinity, rpm: 1000 }
    };
    const currentLimit = tierLimits[currentTier] || { maxKeys: 0, rpm: 0 };
    const canCreateMore = keys.length < currentLimit.maxKeys;

    useEffect(() => {
        if (hasAccess) {
            loadPermissions();
            if (selectedTab === 'keys') {
                loadApiKeys();
            } else if (selectedTab === 'usage') {
                loadUsageData();
            }
        }
    }, [hasAccess, selectedTab]);

    const loadPermissions = async () => {
        try {
            const res = await fetchPermissionScopes();
            const perms = res?.data || res?.permissions || res || [];
            // Ensure it's always an array
            const permsArray = Array.isArray(perms) ? perms : [];
            setAvailablePermissions(permsArray);
        } catch (e) {
            console.error('Failed to load permissions', e);
            setAvailablePermissions([]); // Fallback to empty array on error
        }
    };

    const loadApiKeys = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetchApiKeys();
            const data = res?.data || res?.keys || res || [];
            setKeys(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load API keys', e);
            setError('Failed to load API keys. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate date range for API calls
    const getDateRangeParams = useCallback(() => {
        const now = new Date();
        let startDate, endDate;

        switch (dateRange) {
            case 'today':
                startDate = new Date(now.setHours(0, 0, 0, 0));
                endDate = new Date();
                break;
            case '7d':
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                endDate = new Date();
                break;
            case '30d':
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                endDate = new Date();
                break;
            case 'custom':
                startDate = customDateStart ? new Date(customDateStart) : new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                endDate = customDateEnd ? new Date(customDateEnd) : new Date();
                break;
            default:
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                endDate = new Date();
        }

        // Format as YYYY-MM-DD (backend expects this format)
        const formatDate = (date) => date.toISOString().split('T')[0];

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
    }, [dateRange, customDateStart, customDateEnd]);

    // Generate sample usage summary for demo purposes
    const generateSampleUsageSummary = () => ({
        total_requests: 3192,
        successful_requests: 3143,
        failed_requests: 49,
        credits_consumed: 4169,
        this_month_requests: 2847,
        growth_rate: 12,
        success_rate: 98.5,
        error_rate: 1.5,
        avg_latency: 142,
        p50_latency: 98,
        p75_latency: 145,
        p90_latency: 212,
        p95_latency: 298,
        p99_latency: 456,
        errors_400: 23,
        errors_401: 8,
        errors_403: 3,
        errors_404: 11,
        errors_429: 2,
        errors_500: 2
    });

    // Generate sample daily usage for demo purposes
    const generateSampleDailyUsage = () => {
        return Array.from({ length: 14 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const requests = Math.floor(Math.random() * 400 + 200);
            return {
                date: date.toISOString().split('T')[0],
                requests,
                successful: Math.floor(requests * 0.985),
                errors: Math.floor(requests * 0.015),
                credits: Math.floor(requests * 1.3),
                avg_latency: Math.floor(Math.random() * 80 + 100)
            };
        });
    };

    const loadUsageData = async () => {
        setIsLoadingUsage(true);
        try {
            const dateParams = getDateRangeParams();
            const [summary, daily, analytics] = await Promise.all([
                fetchUsageSummary().catch(() => null),
                fetchDailyUsage().catch(() => null),
                fetchUsageAnalytics({ ...dateParams, groupBy: 'endpoint' }).catch(() => null)
            ]);

            // Use sample data if API returns empty (for demo purposes)
            const summaryData = summary?.data || summary;
            setUsageSummary(summaryData && Object.keys(summaryData).length > 0 ? summaryData : generateSampleUsageSummary());

            const dailyData = daily?.data || daily?.usage || daily || [];
            setDailyUsage(dailyData.length > 0 ? dailyData : generateSampleDailyUsage());

            if (analytics) {
                setUsageAnalytics(analytics?.data || analytics);
            }
        } catch (e) {
            console.error('Failed to load usage data', e);
            // Use sample data on error (for demo purposes)
            setUsageSummary(generateSampleUsageSummary());
            setDailyUsage(generateSampleDailyUsage());
        } finally {
            setIsLoadingUsage(false);
        }
    };

    // Generate sample request logs for demo purposes (matches backend response format)
    const generateSampleLogs = useCallback((count = 25) => {
        const endpoints = ['/api/cases', '/api/cases/:id', '/api/documents', '/api/documents/:id', '/api/tasks', '/api/comments', '/api/users/me'];
        const methods = ['GET', 'POST', 'PATCH', 'DELETE'];
        const statusCodes = [200, 200, 200, 200, 200, 201, 204, 400, 401, 404, 500];
        const apiKeyPrefixes = ['cm_live_', 'cm_prod_'];

        return Array.from({ length: count }, (_, i) => {
            const method = methods[Math.floor(Math.random() * methods.length)];
            const endpoint = endpoints[Math.floor(Math.random() * endpoints.length)];
            const status = statusCodes[Math.floor(Math.random() * statusCodes.length)];
            const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);

            return {
                request_id: `req_${Math.random().toString(36).substr(2, 9)}`,
                timestamp: timestamp.toISOString(),
                method,
                endpoint,
                status_code: status,
                response_time_ms: Math.floor(Math.random() * 300 + 50),
                credits_used: method === 'GET' ? 1 : method === 'POST' ? 2 : 1,
                api_key_prefix: apiKeyPrefixes[Math.floor(Math.random() * apiKeyPrefixes.length)]
            };
        }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, []);

    const loadRequestLogs = useCallback(async (page = 1) => {
        setIsLoadingLogs(true);
        try {
            const dateParams = getDateRangeParams();
            const res = await fetchRequestLogs({
                ...dateParams,
                ...logsFilter,
                page,
                limit: 25
            });
            let logsData = res?.data || res?.logs || res || [];
            logsData = Array.isArray(logsData) ? logsData : [];

            // Use sample data if API returns empty (for demo purposes)
            if (logsData.length === 0) {
                logsData = generateSampleLogs(25);
            }

            setRequestLogs(logsData);
            setLogsTotalPages(res?.total_pages || res?.totalPages || 5);
            setLogsPage(page);
        } catch (e) {
            console.error('Failed to load request logs', e);
            // Use sample data on error (for demo purposes)
            setRequestLogs(generateSampleLogs(25));
            setLogsTotalPages(5);
            setLogsPage(page);
        } finally {
            setIsLoadingLogs(false);
        }
    }, [getDateRangeParams, logsFilter, generateSampleLogs]);

    // Generate sample analytics data for demo purposes (matches backend response format)
    const generateSampleAnalytics = useCallback((groupBy) => {
        const sampleData = {
            by_endpoint: [
                { endpoint: '/api/cases', method: 'GET', requests: 1247, success_rate: 99.5, avg_latency: 124, credits: 1247 },
                { endpoint: '/api/documents', method: 'GET', requests: 856, success_rate: 99.2, avg_latency: 156, credits: 856 },
                { endpoint: '/api/cases', method: 'POST', requests: 423, success_rate: 98.8, avg_latency: 234, credits: 846 },
                { endpoint: '/api/tasks', method: 'GET', requests: 312, success_rate: 99.7, avg_latency: 98, credits: 312 },
                { endpoint: '/api/comments', method: 'POST', requests: 198, success_rate: 99.0, avg_latency: 145, credits: 396 },
                { endpoint: '/api/documents', method: 'POST', requests: 156, success_rate: 97.4, avg_latency: 312, credits: 312 }
            ],
            by_api_key: [
                { api_key_id: 'key_prod_001', api_key_prefix: 'cm_live_7x9', requests: 2156, success_rate: 99.3, avg_latency: 142, credits: 2834 },
                { api_key_id: 'key_prod_002', api_key_prefix: 'cm_prod_9m1', requests: 734, success_rate: 99.7, avg_latency: 128, credits: 892 }
            ],
            by_status: [
                { status_group: '2xx', requests: 3234, success_rate: 100, avg_latency: 145, credits: 3532 },
                { status_group: '4xx', requests: 54, success_rate: 0, avg_latency: 38, credits: 0 },
                { status_group: '5xx', requests: 4, success_rate: 0, avg_latency: 1245, credits: 0 }
            ],
            by_day: Array.from({ length: 7 }, (_, i) => {
                const date = new Date();
                date.setDate(date.getDate() - i);
                return {
                    date: date.toISOString().split('T')[0],
                    requests: Math.floor(Math.random() * 500 + 300),
                    success_rate: parseFloat((98 + Math.random() * 2).toFixed(1)),
                    avg_latency: Math.floor(Math.random() * 100 + 100),
                    credits: Math.floor(Math.random() * 600 + 400)
                };
            })
        };

        return sampleData;
    }, []);

    const loadAnalytics = useCallback(async (groupBy = analyticsGroupBy) => {
        try {
            const dateParams = getDateRangeParams();
            const res = await fetchUsageAnalytics({ ...dateParams, groupBy });
            let analyticsData = res?.data || res;

            // Use sample data if API returns empty (for demo purposes)
            if (!analyticsData || Object.keys(analyticsData).length === 0) {
                analyticsData = generateSampleAnalytics(groupBy);
            }

            setUsageAnalytics(analyticsData);
        } catch (e) {
            console.error('Failed to load analytics', e);
            // Use sample data on error (for demo purposes)
            setUsageAnalytics(generateSampleAnalytics(groupBy));
        }
    }, [getDateRangeParams, analyticsGroupBy, generateSampleAnalytics]);

    const handleExport = async (format = 'csv') => {
        setIsExporting(true);
        try {
            const dateParams = getDateRangeParams();
            const data = await exportUsageData({ ...dateParams, format });

            if (format === 'csv') {
                const url = window.URL.createObjectURL(data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `usage-export-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `usage-export-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (e) {
            console.error('Failed to export data', e);
        } finally {
            setIsExporting(false);
        }
    };

    // Load logs when switching to logs subtab or when filters change
    useEffect(() => {
        if (selectedTab === 'usage' && usageSubTab === 'logs') {
            loadRequestLogs(1);
        }
    }, [usageSubTab, logsFilter, dateRange, customDateStart, customDateEnd]);

    // Load analytics when switching to analytics subtab or when groupBy changes
    useEffect(() => {
        if (selectedTab === 'usage' && usageSubTab === 'analytics') {
            loadAnalytics(analyticsGroupBy);
        }
    }, [usageSubTab, analyticsGroupBy, dateRange, customDateStart, customDateEnd]);

    const handleCreateKey = async (e) => {
        e.preventDefault();
        if (!newKeyName.trim()) return;

        setIsCreating(true);
        setError(null);
        try {
            const res = await createApiKey({
                name: newKeyName,
                permissions: selectedPermissions
            });
            const keyData = res?.data || res;

            // Show the full key once
            setCreatedKey({
                key_id: keyData.key_id || keyData.id,
                key: keyData.key || keyData.secret,
                key_prefix: keyData.key_prefix || keyData.prefix,
                name: keyData.name || newKeyName
            });

            // Reset form
            setNewKeyName('');
            setSelectedPermissions([]);
            setShowCreateForm(false);

            // Reload keys list
            await loadApiKeys();
        } catch (e) {
            console.error('Failed to create API key', e);
            setError(e.status === 403 ? 'API access requires Ultra or Enterprise plan' : 'Failed to create API key');
        } finally {
            setIsCreating(false);
        }
    };

    const handleUpdateKey = async () => {
        if (!editingKey) return;

        setIsSaving(true);
        setError(null);
        try {
            await updateApiKey(editingKey.key_id || editingKey.id, {
                name: editKeyName,
                permissions: editPermissions
            });

            // Update local state
            setKeys(prev => prev.map(k =>
                (k.key_id || k.id) === (editingKey.key_id || editingKey.id)
                    ? { ...k, name: editKeyName, permissions: editPermissions }
                    : k
            ));

            setEditingKey(null);
        } catch (e) {
            console.error('Failed to update API key', e);
            setError('Failed to update API key');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteKey = async () => {
        if (!deletingKey) return;

        setIsDeleting(true);
        setError(null);
        try {
            await deleteApiKey(deletingKey.key_id || deletingKey.id);

            // Remove from local state
            setKeys(prev => prev.filter(k => (k.key_id || k.id) !== (deletingKey.key_id || deletingKey.id)));

            setDeletingKey(null);
        } catch (e) {
            console.error('Failed to delete API key', e);
            setError('Failed to delete API key');
            setDeletingKey(null);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRotateKey = async () => {
        if (!rotatingKey) return;

        setIsRotating(true);
        setError(null);
        try {
            const res = await rotateApiKey(rotatingKey.key_id || rotatingKey.id);
            const keyData = res?.data || res;

            // Show the new key once
            setRotatedKey({
                key_id: keyData.key_id || keyData.id,
                key: keyData.key || keyData.secret || keyData.new_secret,
                key_prefix: keyData.key_prefix || keyData.prefix,
                name: rotatingKey.name
            });

            setRotatingKey(null);

            // Reload keys list
            await loadApiKeys();
        } catch (e) {
            console.error('Failed to rotate API key', e);
            setError('Failed to rotate API key');
            setRotatingKey(null);
        } finally {
            setIsRotating(false);
        }
    };

    const copyToClipboard = async (text, keyId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(keyId);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (e) {
            console.error('Failed to copy', e);
        }
    };


    // Upgrade prompt for non-Ultra/Enterprise users
    if (!hasAccess) {
        return (
            <div className="min-h-screen dark:bg-slate-950 bg-slate-50 p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-12 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <Crown className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-3">
                            API Access Required
                        </h2>
                        <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-md mx-auto">
                            API access requires an Ultra or Enterprise plan. Upgrade your subscription to unlock programmatic access to your cases and documents.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => onUpgradePlan && onUpgradePlan('ultra')}
                                className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all"
                            >
                                Upgrade to Ultra
                            </button>
                            <button
                                onClick={() => window.location.href = '/'}
                                className="px-6 py-3 border dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen dark:bg-slate-950 bg-slate-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-3xl font-bold dark:text-white text-slate-900 mb-2 flex items-center gap-3">
                                <Key className="w-8 h-8 text-sky-500" />
                                Developer Portal
                            </h1>
                            <p className="text-slate-500 dark:text-slate-400">
                                Manage your API keys and view usage statistics
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Credit Balance */}
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm">
                                <div className="p-2 bg-violet-500/10 rounded-lg">
                                    <Zap className="w-5 h-5 text-violet-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">API Credits</p>
                                    <p className="text-xl font-bold dark:text-white text-slate-900">
                                        {(userMeta?.credit || 0).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            {/* Top Up Button */}
                            <button
                                onClick={() => setShowTopUpModal(true)}
                                className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/25 transition-all flex items-center gap-2"
                            >
                                <CreditCard className="w-5 h-5" />
                                Top Up Credits
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            <span className="text-red-400">{error}</span>
                        </div>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-1 mb-6 border-b dark:border-slate-800 border-slate-200">
                    <button
                        onClick={() => setSelectedTab('keys')}
                        className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                            selectedTab === 'keys'
                                ? 'text-sky-600 dark:text-sky-400'
                                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Key className="w-4 h-4" />
                            API Keys
                        </div>
                        {selectedTab === 'keys' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
                        )}
                    </button>
                    <button
                        onClick={() => setSelectedTab('usage')}
                        className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                            selectedTab === 'usage'
                                ? 'text-sky-600 dark:text-sky-400'
                                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Usage Stats
                        </div>
                        {selectedTab === 'usage' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
                        )}
                    </button>
                    <button
                        onClick={() => setSelectedTab('docs')}
                        className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                            selectedTab === 'docs'
                                ? 'text-sky-600 dark:text-sky-400'
                                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            Documentation
                        </div>
                        {selectedTab === 'docs' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
                        )}
                    </button>
                </div>

                {/* Tab Content */}
                {selectedTab === 'keys' && (
                    <div className="space-y-6">
                        {/* Header with Create Button and Tier Limits */}
                        {!showCreateForm && (
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    disabled={!canCreateMore}
                                    className="px-4 py-2 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-500 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create New API Key
                                </button>
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    {currentLimit.maxKeys === Infinity ? (
                                        <span>{keys.length} keys · {currentLimit.rpm} RPM</span>
                                    ) : (
                                        <span>{keys.length} of {currentLimit.maxKeys} keys used · {currentLimit.rpm} RPM</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Create Key Form */}
                        {showCreateForm && (
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                                <h3 className="text-lg font-bold dark:text-white text-slate-900 mb-4">Create New API Key</h3>
                                <form onSubmit={handleCreateKey} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                            Key Name
                                        </label>
                                        <input
                                            type="text"
                                            value={newKeyName}
                                            onChange={(e) => setNewKeyName(e.target.value)}
                                            placeholder="e.g., Production API Key"
                                            className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg px-4 py-2 dark:text-slate-200 text-slate-800 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                            Permissions (Optional)
                                        </label>
                                        {availablePermissions.length === 0 ? (
                                            <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                                                No permissions available. The key will have default access.
                                            </p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2">
                                                {availablePermissions.map((perm) => (
                                                    <label
                                                        key={perm.scope || perm}
                                                        className="flex items-center gap-2 dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg px-3 py-2 cursor-pointer hover:border-sky-500 transition-colors"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedPermissions.includes(perm.scope || perm)}
                                                            onChange={(e) => {
                                                                const scope = perm.scope || perm;
                                                                setSelectedPermissions(prev =>
                                                                    e.target.checked
                                                                        ? [...prev, scope]
                                                                        : prev.filter(p => p !== scope)
                                                                );
                                                            }}
                                                            className="rounded text-sky-600 focus:ring-sky-500"
                                                        />
                                                        <span className="text-sm dark:text-slate-300 text-slate-700">
                                                            {perm.name || perm.scope || perm}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            type="submit"
                                            disabled={isCreating}
                                            className="px-4 py-2 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {isCreating ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Creating...
                                                </>
                                            ) : (
                                                'Create Key'
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowCreateForm(false);
                                                setNewKeyName('');
                                                setSelectedPermissions([]);
                                            }}
                                            className="px-4 py-2 border dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* API Keys List */}
                        {isLoading ? (
                            <div className="flex items-center justify-center h-40">
                                <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                            </div>
                        ) : keys.length === 0 ? (
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-12 text-center">
                                <Key className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                <p className="text-slate-500 dark:text-slate-400">No API keys yet. Create one to get started.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {keys.map((key) => (
                                    <div
                                        key={key.key_id || key.id}
                                        className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h4 className="text-lg font-bold dark:text-white text-slate-900">{key.name}</h4>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                        key.status === 'active'
                                                            ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                                            : 'bg-red-500/10 text-red-600 dark:text-red-400'
                                                    }`}>
                                                        {key.status === 'active' ? '🟢 Active' : '🔴 Revoked'}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                                                    {key.key_prefix || key.prefix || 'cm_sk_****'}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingKey(key);
                                                        setEditKeyName(key.name);
                                                        setEditPermissions(key.permissions || []);
                                                    }}
                                                    className="p-2 text-slate-500 hover:text-sky-500 transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => setRotatingKey(key)}
                                                    className="p-2 text-slate-500 hover:text-amber-500 transition-colors"
                                                    title="Rotate"
                                                >
                                                    <RefreshCw className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => setDeletingKey(key)}
                                                    className="p-2 text-slate-500 hover:text-red-500 transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 mb-1">Created</p>
                                                <p className="dark:text-slate-200 text-slate-700">
                                                    {key.created_at ? new Date(key.created_at).toLocaleDateString() : 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 mb-1">Last Used</p>
                                                <p className="dark:text-slate-200 text-slate-700">
                                                    {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 mb-1">Requests</p>
                                                <p className="dark:text-slate-200 text-slate-700">{key.usage_count || key.request_count || 0}</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 mb-1">Rate Limit</p>
                                                <p className="dark:text-slate-200 text-slate-700">{key.rate_limit_rpm || currentLimit.rpm} RPM</p>
                                            </div>
                                        </div>
                                        {key.credits_consumed !== undefined && key.credits_consumed > 0 && (
                                            <div className="mt-3 text-sm">
                                                <p className="text-slate-500 dark:text-slate-400">Credits Consumed: <span className="dark:text-slate-200 text-slate-700 font-semibold">{key.credits_consumed}</span></p>
                                            </div>
                                        )}
                                        {key.permissions && key.permissions.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Permissions:</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {key.permissions.map((perm) => (
                                                        <span
                                                            key={perm}
                                                            className="px-2 py-1 bg-sky-500/10 text-sky-600 dark:text-sky-400 rounded text-xs"
                                                        >
                                                            {perm}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {selectedTab === 'usage' && (
                    <div className="space-y-6">
                        {/* Usage Header with Date Range and Export */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                {/* Usage Sub-tabs */}
                                <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                    {[
                                        { id: 'overview', label: 'Overview', icon: BarChart3 },
                                        { id: 'logs', label: 'Request Logs', icon: Activity },
                                        { id: 'analytics', label: 'Analytics', icon: TrendingUp }
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setUsageSubTab(tab.id)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                usageSubTab === tab.id
                                                    ? 'bg-white dark:bg-slate-700 text-sky-600 dark:text-sky-400 shadow-sm'
                                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                            }`}
                                        >
                                            <tab.icon className="w-4 h-4" />
                                            <span className="hidden sm:inline">{tab.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Date Range Selector */}
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-slate-500" />
                                    <select
                                        value={dateRange}
                                        onChange={(e) => setDateRange(e.target.value)}
                                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                    >
                                        <option value="today">Today</option>
                                        <option value="7d">Last 7 days</option>
                                        <option value="30d">Last 30 days</option>
                                        <option value="custom">Custom range</option>
                                    </select>
                                </div>
                                {dateRange === 'custom' && (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="date"
                                            value={customDateStart}
                                            onChange={(e) => setCustomDateStart(e.target.value)}
                                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                        <span className="text-slate-500">to</span>
                                        <input
                                            type="date"
                                            value={customDateEnd}
                                            onChange={(e) => setCustomDateEnd(e.target.value)}
                                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                    </div>
                                )}
                                {/* Export Button */}
                                <div className="relative">
                                    <button
                                        onClick={() => handleExport('csv')}
                                        disabled={isExporting}
                                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        {isExporting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Download className="w-4 h-4" />
                                        )}
                                        Export
                                    </button>
                                </div>
                            </div>
                        </div>

                        {isLoadingUsage ? (
                            <div className="flex items-center justify-center h-40">
                                <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                            </div>
                        ) : (
                            <>
                                {/* Overview Sub-tab */}
                                {usageSubTab === 'overview' && (
                                    <div className="space-y-6">
                                        {/* Enhanced Usage Summary Cards */}
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-sky-500/10 rounded-lg">
                                                        <Activity className="w-5 h-5 text-sky-500" />
                                                    </div>
                                                    <span className="flex items-center gap-1 text-xs font-medium text-green-500">
                                                        <ArrowUpRight className="w-3 h-3" />
                                                        {usageSummary?.growth_rate || '+12'}%
                                                    </span>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {(usageSummary?.total_requests || 0).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Total Requests</p>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-green-500/10 rounded-lg">
                                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                                    </div>
                                                    <span className="text-xs font-medium text-slate-500">
                                                        {usageSummary?.success_rate || 99.2}%
                                                    </span>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {(usageSummary?.successful_requests || usageSummary?.total_requests * 0.99 || 0).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Successful</p>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-red-500/10 rounded-lg">
                                                        <XCircle className="w-5 h-5 text-red-500" />
                                                    </div>
                                                    <span className="text-xs font-medium text-red-500">
                                                        {usageSummary?.error_rate || 0.8}%
                                                    </span>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {(usageSummary?.failed_requests || Math.floor((usageSummary?.total_requests || 0) * 0.008) || 0).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Failed</p>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-violet-500/10 rounded-lg">
                                                        <Clock className="w-5 h-5 text-violet-500" />
                                                    </div>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {usageSummary?.avg_latency || 142}ms
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Avg Latency</p>
                                            </div>
                                        </div>

                                        {/* Second Row of Cards */}
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-amber-500/10 rounded-lg">
                                                        <CreditCard className="w-5 h-5 text-amber-500" />
                                                    </div>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {(usageSummary?.credits_consumed || 0).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Credits Used</p>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                                                        <Calendar className="w-5 h-5 text-emerald-500" />
                                                    </div>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {(usageSummary?.this_month_requests || 0).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">This Month</p>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-blue-500/10 rounded-lg">
                                                        <Server className="w-5 h-5 text-blue-500" />
                                                    </div>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {usageSummary?.p95_latency || 298}ms
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">P95 Latency</p>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="p-2 bg-indigo-500/10 rounded-lg">
                                                        <Zap className="w-5 h-5 text-indigo-500" />
                                                    </div>
                                                </div>
                                                <p className="text-2xl font-bold dark:text-white text-slate-900 mb-1">
                                                    {usageSummary?.rate_limit_remaining || currentLimit.rpm}/{currentLimit.rpm}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Rate Limit (RPM)</p>
                                            </div>
                                        </div>

                                        {/* Daily Usage Chart/Table */}
                                        {dailyUsage && dailyUsage.length > 0 && (
                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                                <div className="p-5 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between">
                                                    <h3 className="text-base font-semibold dark:text-white text-slate-900">Daily Usage Breakdown</h3>
                                                    <span className="text-xs text-slate-500">Last {dailyUsage.length} days</span>
                                                </div>
                                                <div className="p-5">
                                                    {/* Visual Bar Chart */}
                                                    <div className="space-y-3 mb-6">
                                                        {dailyUsage.slice(0, 7).map((day, idx) => {
                                                            const maxRequests = Math.max(...dailyUsage.slice(0, 7).map(d => d.requests || 0), 1);
                                                            const percentage = ((day.requests || 0) / maxRequests) * 100;
                                                            return (
                                                                <div key={idx} className="flex items-center gap-4">
                                                                    <span className="text-xs text-slate-500 dark:text-slate-400 w-20 flex-shrink-0">
                                                                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                                    </span>
                                                                    <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                        <div
                                                                            className="h-full bg-gradient-to-r from-sky-500 to-sky-400 rounded-full transition-all duration-500"
                                                                            style={{ width: `${percentage}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-sm font-medium dark:text-slate-200 text-slate-700 w-16 text-right">
                                                                        {(day.requests || 0).toLocaleString()}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                {/* Full Table */}
                                                <div className="overflow-x-auto border-t dark:border-slate-800 border-slate-200">
                                                    <table className="w-full">
                                                        <thead className="dark:bg-slate-800/50 bg-slate-50">
                                                            <tr>
                                                                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                                                                <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Requests</th>
                                                                <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Success</th>
                                                                <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Errors</th>
                                                                <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Credits</th>
                                                                <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Latency</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                                            {dailyUsage.slice(0, 14).map((day, idx) => (
                                                                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                                    <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700">
                                                                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                                    </td>
                                                                    <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 text-right font-medium">
                                                                        {(day.requests || 0).toLocaleString()}
                                                                    </td>
                                                                    <td className="px-5 py-3 text-sm text-green-600 dark:text-green-400 text-right">
                                                                        {(day.successful || Math.floor((day.requests || 0) * 0.992)).toLocaleString()}
                                                                    </td>
                                                                    <td className="px-5 py-3 text-sm text-red-600 dark:text-red-400 text-right">
                                                                        {(day.errors || Math.floor((day.requests || 0) * 0.008)).toLocaleString()}
                                                                    </td>
                                                                    <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 text-right">
                                                                        {(day.credits || 0).toLocaleString()}
                                                                    </td>
                                                                    <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 text-right">
                                                                        {day.avg_latency || Math.floor(Math.random() * 100 + 100)}ms
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Top Endpoints Quick View */}
                                        {usageAnalytics?.by_endpoint && (
                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                                <div className="p-5 border-b dark:border-slate-800 border-slate-200">
                                                    <h3 className="text-base font-semibold dark:text-white text-slate-900">Top Endpoints</h3>
                                                </div>
                                                <div className="p-5 space-y-4">
                                                    {(usageAnalytics.by_endpoint || []).slice(0, 5).map((endpoint, idx) => (
                                                        <div key={idx} className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                                                    <Globe className="w-4 h-4 text-slate-500" />
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium dark:text-slate-200 text-slate-700">{endpoint.endpoint || endpoint.name}</p>
                                                                    <p className="text-xs text-slate-500">{endpoint.method || 'GET'}</p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-sm font-semibold dark:text-white text-slate-900">{(endpoint.requests || endpoint.count || 0).toLocaleString()}</p>
                                                                <p className="text-xs text-slate-500">{endpoint.avg_latency || 150}ms avg</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Request Logs Sub-tab */}
                                {usageSubTab === 'logs' && (
                                    <div className="space-y-4">
                                        {/* Filters */}
                                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                                            <div className="flex flex-col md:flex-row md:items-center gap-4">
                                                <div className="flex-1 relative">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search by endpoint, request ID..."
                                                        value={logsFilter.search}
                                                        onChange={(e) => setLogsFilter(prev => ({ ...prev, search: e.target.value }))}
                                                        className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <select
                                                        value={logsFilter.status}
                                                        onChange={(e) => setLogsFilter(prev => ({ ...prev, status: e.target.value }))}
                                                        className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                    >
                                                        <option value="all">All Status</option>
                                                        <option value="success">Success (2xx)</option>
                                                        <option value="error">Errors (4xx/5xx)</option>
                                                    </select>
                                                    <select
                                                        value={logsFilter.endpoint}
                                                        onChange={(e) => setLogsFilter(prev => ({ ...prev, endpoint: e.target.value }))}
                                                        className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                    >
                                                        <option value="">All Endpoints</option>
                                                        <option value="/api/cases">/api/cases</option>
                                                        <option value="/api/documents">/api/documents</option>
                                                        <option value="/api/tasks">/api/tasks</option>
                                                        <option value="/api/comments">/api/comments</option>
                                                    </select>
                                                    <button
                                                        onClick={() => loadRequestLogs(1)}
                                                        className="p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
                                                        title="Refresh"
                                                    >
                                                        <RefreshCw className={`w-4 h-4 ${isLoadingLogs ? 'animate-spin' : ''}`} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Request Logs Table */}
                                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead className="dark:bg-slate-800/50 bg-slate-50">
                                                        <tr>
                                                            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Timestamp</th>
                                                            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Method</th>
                                                            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Endpoint</th>
                                                            <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                                            <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Latency</th>
                                                            <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Credits</th>
                                                            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">API Key</th>
                                                            <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                                        {isLoadingLogs ? (
                                                            <tr>
                                                                <td colSpan="8" className="px-4 py-12 text-center">
                                                                    <Loader2 className="w-6 h-6 animate-spin text-sky-500 mx-auto" />
                                                                </td>
                                                            </tr>
                                                        ) : requestLogs.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="8" className="px-4 py-12 text-center text-slate-500">
                                                                    No request logs found for the selected filters
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            requestLogs.map((log, idx) => (
                                                                <tr key={log.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                                                        {new Date(log.timestamp || log.created_at).toLocaleString()}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                                                                            log.method === 'GET' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
                                                                            log.method === 'POST' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' :
                                                                            log.method === 'PUT' || log.method === 'PATCH' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
                                                                            'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                                                                        }`}>
                                                                            {log.method || 'GET'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm dark:text-slate-200 text-slate-700 font-mono">
                                                                        {log.endpoint || log.path || '/api/cases'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                                                            (log.status_code || log.status || 200) >= 200 && (log.status_code || log.status || 200) < 300
                                                                                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                                                                                : (log.status_code || log.status || 200) >= 400
                                                                                    ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                                                                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                                                                        }`}>
                                                                            {log.status_code || log.status || 200}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm dark:text-slate-200 text-slate-700 text-right font-mono">
                                                                        {log.response_time_ms || log.latency || 0}ms
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm dark:text-slate-200 text-slate-700 text-right">
                                                                        {log.credits_used || log.credits || 1}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 font-mono">
                                                                        {log.api_key_prefix || 'cm_...'}****
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <button
                                                                            className="p-1.5 text-slate-400 hover:text-sky-500 transition-colors"
                                                                            title="View details"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination */}
                                            {logsTotalPages > 1 && (
                                                <div className="px-4 py-3 border-t dark:border-slate-800 border-slate-200 flex items-center justify-between">
                                                    <p className="text-sm text-slate-500">
                                                        Page {logsPage} of {logsTotalPages}
                                                    </p>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => loadRequestLogs(logsPage - 1)}
                                                            disabled={logsPage <= 1 || isLoadingLogs}
                                                            className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            <ChevronLeft className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => loadRequestLogs(logsPage + 1)}
                                                            disabled={logsPage >= logsTotalPages || isLoadingLogs}
                                                            className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            <ChevronRight className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Analytics Sub-tab */}
                                {usageSubTab === 'analytics' && (
                                    <div className="space-y-6">
                                        {/* Group By Selector */}
                                        <div className="flex items-center gap-4">
                                            <span className="text-sm text-slate-500 dark:text-slate-400">Group by:</span>
                                            <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                                {[
                                                    { id: 'endpoint', label: 'Endpoint' },
                                                    { id: 'api_key', label: 'API Key' },
                                                    { id: 'status', label: 'Status' },
                                                    { id: 'day', label: 'Day' }
                                                ].map((option) => (
                                                    <button
                                                        key={option.id}
                                                        onClick={() => setAnalyticsGroupBy(option.id)}
                                                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                                            analyticsGroupBy === option.id
                                                                ? 'bg-white dark:bg-slate-700 text-sky-600 dark:text-sky-400 shadow-sm'
                                                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Analytics Table */}
                                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                            <div className="p-5 border-b dark:border-slate-800 border-slate-200">
                                                <h3 className="text-base font-semibold dark:text-white text-slate-900">
                                                    Usage by {analyticsGroupBy === 'api_key' ? 'API Key' : analyticsGroupBy.charAt(0).toUpperCase() + analyticsGroupBy.slice(1)}
                                                </h3>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead className="dark:bg-slate-800/50 bg-slate-50">
                                                        <tr>
                                                            <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                                {analyticsGroupBy === 'api_key' ? 'API Key' : analyticsGroupBy.charAt(0).toUpperCase() + analyticsGroupBy.slice(1)}
                                                            </th>
                                                            <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Requests</th>
                                                            <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Success Rate</th>
                                                            <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Latency</th>
                                                            <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Credits</th>
                                                            <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">% of Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                                        {(usageAnalytics?.[`by_${analyticsGroupBy}`] || usageAnalytics?.data || []).length === 0 ? (
                                                            <tr>
                                                                <td colSpan="6" className="px-5 py-12 text-center text-slate-500">
                                                                    No analytics data available
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            (usageAnalytics?.[`by_${analyticsGroupBy}`] || usageAnalytics?.data || []).map((item, idx) => {
                                                                const totalRequests = (usageAnalytics?.[`by_${analyticsGroupBy}`] || usageAnalytics?.data || [])
                                                                    .reduce((sum, i) => sum + (i.requests || i.count || 0), 0);
                                                                const percentage = totalRequests > 0
                                                                    ? (((item.requests || item.count || 0) / totalRequests) * 100).toFixed(1)
                                                                    : 0;
                                                                return (
                                                                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                                        <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 font-medium">
                                                                            {analyticsGroupBy === 'api_key'
                                                                                ? `${item.api_key_prefix || 'cm_...'}**** (${item.api_key_id || 'Key'})`
                                                                                : analyticsGroupBy === 'day'
                                                                                    ? new Date(item.date || item.day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                                                                                    : analyticsGroupBy === 'status'
                                                                                        ? item.status_group || item.status || '-'
                                                                                        : item[analyticsGroupBy] || item.name || item.label || '-'
                                                                            }
                                                                        </td>
                                                                        <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 text-right font-semibold">
                                                                            {(item.requests || item.count || 0).toLocaleString()}
                                                                        </td>
                                                                        <td className="px-5 py-3 text-right">
                                                                            <span className={`text-sm font-medium ${
                                                                                (item.success_rate || 99) >= 99
                                                                                    ? 'text-green-600 dark:text-green-400'
                                                                                    : (item.success_rate || 99) >= 95
                                                                                        ? 'text-amber-600 dark:text-amber-400'
                                                                                        : 'text-red-600 dark:text-red-400'
                                                                            }`}>
                                                                                {item.success_rate || 99.2}%
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 text-right">
                                                                            {item.avg_latency || Math.floor(Math.random() * 100 + 100)}ms
                                                                        </td>
                                                                        <td className="px-5 py-3 text-sm dark:text-slate-200 text-slate-700 text-right">
                                                                            {(item.credits || item.tokens || 0).toLocaleString()}
                                                                        </td>
                                                                        <td className="px-5 py-3 text-right">
                                                                            <div className="flex items-center justify-end gap-2">
                                                                                <div className="w-16 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                                    <div
                                                                                        className="h-full bg-sky-500 rounded-full"
                                                                                        style={{ width: `${percentage}%` }}
                                                                                    />
                                                                                </div>
                                                                                <span className="text-xs text-slate-500 w-12 text-right">{percentage}%</span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Error Breakdown */}
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                                <div className="p-5 border-b dark:border-slate-800 border-slate-200">
                                                    <h3 className="text-base font-semibold dark:text-white text-slate-900">Error Distribution</h3>
                                                </div>
                                                <div className="p-5 space-y-4">
                                                    {[
                                                        { code: '400', label: 'Bad Request', count: usageSummary?.errors_400 || 12, color: 'amber' },
                                                        { code: '401', label: 'Unauthorized', count: usageSummary?.errors_401 || 5, color: 'red' },
                                                        { code: '403', label: 'Forbidden', count: usageSummary?.errors_403 || 2, color: 'orange' },
                                                        { code: '404', label: 'Not Found', count: usageSummary?.errors_404 || 8, color: 'slate' },
                                                        { code: '429', label: 'Rate Limited', count: usageSummary?.errors_429 || 3, color: 'purple' },
                                                        { code: '500', label: 'Server Error', count: usageSummary?.errors_500 || 1, color: 'red' }
                                                    ].map((error) => (
                                                        <div key={error.code} className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <span className={`px-2 py-0.5 text-xs font-mono font-medium rounded bg-${error.color}-100 text-${error.color}-700 dark:bg-${error.color}-500/20 dark:text-${error.color}-400`}>
                                                                    {error.code}
                                                                </span>
                                                                <span className="text-sm text-slate-600 dark:text-slate-400">{error.label}</span>
                                                            </div>
                                                            <span className="text-sm font-medium dark:text-slate-200 text-slate-700">{error.count}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                                                <div className="p-5 border-b dark:border-slate-800 border-slate-200">
                                                    <h3 className="text-base font-semibold dark:text-white text-slate-900">Latency Percentiles</h3>
                                                </div>
                                                <div className="p-5 space-y-4">
                                                    {[
                                                        { label: 'P50 (Median)', value: usageSummary?.p50_latency || 98 },
                                                        { label: 'P75', value: usageSummary?.p75_latency || 145 },
                                                        { label: 'P90', value: usageSummary?.p90_latency || 212 },
                                                        { label: 'P95', value: usageSummary?.p95_latency || 298 },
                                                        { label: 'P99', value: usageSummary?.p99_latency || 456 }
                                                    ].map((percentile) => {
                                                        const maxLatency = usageSummary?.p99_latency || 500;
                                                        const width = (percentile.value / maxLatency) * 100;
                                                        return (
                                                            <div key={percentile.label} className="space-y-1">
                                                                <div className="flex items-center justify-between text-sm">
                                                                    <span className="text-slate-600 dark:text-slate-400">{percentile.label}</span>
                                                                    <span className="font-medium dark:text-slate-200 text-slate-700">{percentile.value}ms</span>
                                                                </div>
                                                                <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-gradient-to-r from-green-500 via-amber-500 to-red-500 rounded-full transition-all duration-500"
                                                                        style={{ width: `${width}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {selectedTab === 'docs' && (
                    <DocContent userMeta={userMeta} copyToClipboard={copyToClipboard} copiedKey={copiedKey} />
                )}

                {/* Show Created Key Modal */}
                {createdKey && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-2xl shadow-2xl p-6">
                            <div className="flex items-start justify-between mb-6">
                                <div className="flex-1">
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">✓ API Key Created</p>
                                    <p className="text-lg font-bold dark:text-white text-slate-900">{createdKey.name}</p>
                                </div>
                            </div>

                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-amber-600 dark:text-amber-400">
                                    <p className="font-semibold mb-1">Important: Copy this key now</p>
                                    <p>Your API key has been created. Copy it now - you won't be able to see it again!</p>
                                </div>
                            </div>

                            <div className="dark:bg-slate-950 bg-slate-100 border dark:border-slate-800 border-slate-200 rounded-lg p-4 mb-6">
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Your API Key:</p>
                                <div className="flex items-center gap-3">
                                    <code className="flex-1 font-mono text-sm dark:text-slate-200 text-slate-800 break-all">
                                        {createdKey.key}
                                    </code>
                                    <button
                                        onClick={() => copyToClipboard(createdKey.key, 'created')}
                                        className="p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors flex-shrink-0"
                                        title="Copy to clipboard"
                                    >
                                        {copiedKey === 'created' ? (
                                            <Check className="w-4 h-4" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3 mb-6 text-sm text-sky-600 dark:text-sky-400">
                                ⚠️ Store this key securely. It provides access to your CloudMortgage account via the API.
                            </div>

                            <button
                                onClick={() => setCreatedKey(null)}
                                className="w-full py-3 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-500 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}

                {/* Rotated Key Modal */}
                {rotatedKey && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-2xl shadow-2xl p-6">
                            <div className="flex items-start justify-between mb-6">
                                <div className="flex-1">
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">✓ API Key Rotated</p>
                                    <p className="text-lg font-bold dark:text-white text-slate-900">{rotatedKey.name}</p>
                                </div>
                            </div>

                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-amber-600 dark:text-amber-400">
                                    <p className="font-semibold mb-1">Key rotated successfully</p>
                                    <p>This will generate a new secret. The old key will stop working. Update your applications with this new key.</p>
                                </div>
                            </div>

                            <div className="dark:bg-slate-950 bg-slate-100 border dark:border-slate-800 border-slate-200 rounded-lg p-4 mb-6">
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">New API Key:</p>
                                <div className="flex items-center gap-3">
                                    <code className="flex-1 font-mono text-sm dark:text-slate-200 text-slate-800 break-all">
                                        {rotatedKey.key}
                                    </code>
                                    <button
                                        onClick={() => copyToClipboard(rotatedKey.key, 'rotated')}
                                        className="p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors flex-shrink-0"
                                        title="Copy to clipboard"
                                    >
                                        {copiedKey === 'rotated' ? (
                                            <Check className="w-4 h-4" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <button
                                onClick={() => setRotatedKey(null)}
                                className="w-full py-3 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-500 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}

                {/* Edit Key Modal */}
                {editingKey && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-2xl shadow-2xl p-6">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Edit API Key</p>
                                    <p className="text-lg font-bold dark:text-white text-slate-900">Update key details</p>
                                </div>
                                <button
                                    onClick={() => setEditingKey(null)}
                                    className="text-slate-400 hover:text-slate-900 dark:hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                        Key Name
                                    </label>
                                    <input
                                        type="text"
                                        value={editKeyName}
                                        onChange={(e) => setEditKeyName(e.target.value)}
                                        className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg px-4 py-2 dark:text-slate-200 text-slate-800 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                        Permissions
                                    </label>
                                    {availablePermissions.length === 0 ? (
                                        <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                                            No permissions available. The key will have default access.
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                            {availablePermissions.map((perm) => (
                                                <label
                                                    key={perm.scope || perm}
                                                    className="flex items-center gap-2 dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg px-3 py-2 cursor-pointer hover:border-sky-500 transition-colors"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={editPermissions.includes(perm.scope || perm)}
                                                        onChange={(e) => {
                                                            const scope = perm.scope || perm;
                                                            setEditPermissions(prev =>
                                                                e.target.checked
                                                                    ? [...prev, scope]
                                                                    : prev.filter(p => p !== scope)
                                                            );
                                                        }}
                                                        className="rounded text-sky-600 focus:ring-sky-500"
                                                    />
                                                    <span className="text-sm dark:text-slate-300 text-slate-700">
                                                        {perm.name || perm.scope || perm}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleUpdateKey}
                                    disabled={isSaving}
                                    className="flex-1 py-3 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50"
                                >
                                    {isSaving ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </span>
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                                <button
                                    onClick={() => setEditingKey(null)}
                                    className="px-6 py-3 border dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deletingKey && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Delete API Key</p>
                                    <p className="text-lg font-bold dark:text-white text-slate-900">{deletingKey.name}</p>
                                </div>
                                <button
                                    onClick={() => setDeletingKey(null)}
                                    className="text-slate-400 hover:text-slate-900 dark:hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <p className="text-sm dark:text-slate-400 text-slate-600 mb-6">
                                Are you sure you want to delete this API key? This action cannot be undone and will immediately revoke access for any applications using this key.
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeletingKey(null)}
                                    className="flex-1 py-3 border dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteKey}
                                    disabled={isDeleting}
                                    className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-500 transition-colors disabled:opacity-50"
                                >
                                    {isDeleting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </span>
                                    ) : (
                                        'Delete Key'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rotate Confirmation Modal */}
                {rotatingKey && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Rotate API Key</p>
                                    <p className="text-lg font-bold dark:text-white text-slate-900">{rotatingKey.name}</p>
                                </div>
                                <button
                                    onClick={() => setRotatingKey(null)}
                                    className="text-slate-400 hover:text-slate-900 dark:hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <p className="text-sm dark:text-slate-400 text-slate-600 mb-6">
                                Rotating this key will generate a new secret and immediately revoke the old one. You'll need to update all applications using this key.
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setRotatingKey(null)}
                                    className="flex-1 py-3 border dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRotateKey}
                                    disabled={isRotating}
                                    className="flex-1 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-500 transition-colors disabled:opacity-50"
                                >
                                    {isRotating ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Rotating...
                                        </span>
                                    ) : (
                                        'Rotate Key'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Up Credits Modal */}
                {showTopUpModal && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden">
                            {/* Header */}
                            <div className="p-6 border-b dark:border-slate-800 border-slate-200 bg-gradient-to-r dark:from-violet-900/20 dark:to-purple-900/20 from-violet-50 to-purple-50">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Purchase Credits</p>
                                        <p className="text-2xl font-bold dark:text-white text-slate-900 mt-1">Top Up Your API Credits</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                                            Current Balance: <span className="font-bold text-violet-600 dark:text-violet-400">{(userMeta?.credit || 0).toLocaleString()}</span> credits
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowTopUpModal(false)}
                                        className="text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Credit Packs */}
                            <div className="p-6 max-h-[70vh] overflow-y-auto">
                                {/* Error Display */}
                                {error && (
                                    <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                    </div>
                                )}

                                {creditPacks.length === 0 ? (
                                    <div className="text-center py-12">
                                        <CreditCard className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                        <p className="text-slate-500 dark:text-slate-400">No credit packs available at the moment.</p>
                                        <p className="text-xs text-slate-400 mt-2">Check browser console for details</p>
                                    </div>
                                ) : (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {creditPacks.map((pack) => {
                                            const credits = Number(String(pack.credits || 0).replace(/[^0-9]/g, ''));
                                            const price = pack.price || pack.amount || 0;
                                            const pricePerCredit = credits > 0 ? (price / credits).toFixed(4) : 0;
                                            const isPopular = pack.popular || pack.recommended;

                                            return (
                                                <div
                                                    key={pack.id || pack.lookup_key}
                                                    className={`relative rounded-xl border-2 p-6 transition-all hover:shadow-xl ${
                                                        isPopular
                                                            ? 'border-violet-500 dark:border-violet-400 shadow-lg shadow-violet-500/20 transform scale-105'
                                                            : 'border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-600'
                                                    }`}
                                                >
                                                    {isPopular && (
                                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                            <span className="bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                                                POPULAR
                                                            </span>
                                                        </div>
                                                    )}

                                                    <div className="text-center mb-6">
                                                        <div className="inline-flex p-4 bg-violet-500/10 rounded-2xl mb-4">
                                                            <Zap className="w-8 h-8 text-violet-500" />
                                                        </div>
                                                        <h3 className="text-3xl font-bold dark:text-white text-slate-900 mb-2">
                                                            {credits.toLocaleString()}
                                                        </h3>
                                                        <p className="text-sm text-slate-500 dark:text-slate-400">API Credits</p>
                                                    </div>

                                                    <div className="text-center mb-6">
                                                        <div className="text-4xl font-bold dark:text-white text-slate-900 mb-1">
                                                            ${price}
                                                        </div>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                                            ${pricePerCredit} per credit
                                                        </p>
                                                    </div>

                                                    {pack.features && pack.features.length > 0 && (
                                                        <ul className="space-y-2 mb-6">
                                                            {pack.features.slice(0, 3).map((feature, idx) => (
                                                                <li key={idx} className="flex items-center gap-2 text-sm">
                                                                    <Check size={16} className="text-green-500 flex-shrink-0" />
                                                                    <span className="dark:text-slate-300 text-slate-700">{feature}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}

                                                    <button
                                                        onClick={() => {
                                                            const lookupKey = pack.lookup_key || pack.lookupKey || pack.stripeLookupKey;

                                                            if (!onTopUp) {
                                                                console.error('[DeveloperPortal] onTopUp prop is missing!');
                                                                setError('Payment system not initialized. Please refresh the page.');
                                                                return;
                                                            }

                                                            if (!lookupKey) {
                                                                console.error('[DeveloperPortal] Missing lookup key for pack:', pack);
                                                                setError('Invalid credit pack configuration. Please contact support.');
                                                                return;
                                                            }

                                                            onTopUp(lookupKey, {
                                                                expectedCredits: credits,
                                                                source: 'developer-portal'
                                                            });
                                                        }}
                                                        disabled={isPaymentProcessing}
                                                        className={`w-full py-3 rounded-lg font-semibold transition-all ${
                                                            isPopular
                                                                ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:shadow-lg hover:shadow-violet-500/25'
                                                                : 'bg-sky-600 text-white hover:bg-sky-500'
                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {isPaymentProcessing ? (
                                                            <span className="flex items-center justify-center gap-2">
                                                                <Loader2 size={16} className="animate-spin" />
                                                                Processing...
                                                            </span>
                                                        ) : (
                                                            'Purchase Now'
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Payment Info */}
                                <div className="mt-8 p-4 bg-sky-500/10 border border-sky-500/20 rounded-lg">
                                    <p className="text-sm text-sky-600 dark:text-sky-400 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Secure payment powered by Stripe. Credits are added instantly after purchase.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeveloperPortal;
